<template>
    <b-modal
        :id="'paper-powerpoint-plagiarism-modal-' + editorFileIdIn"
        size="lg"
        :title="$t('roadmap.plagiarism_reports')"
        :ok-title="$t('common.close')"
        ok-only
        modal-class="apprentice-default-modal"
        @show="onShow"
        @hide="onHide">
        <design-2021-apprentice-data-table
            v-if="reports.length"
            :items="reports"
            :fields="fields"
            :nosearch="true">
            <template v-slot:options>
                <div class="text-right">
                    <b-button
                        class="btn-add"
                        variant="primary"
                        @click="submitForPlagiarismCheck">
                        {{ $t('roadmap.request_new_check') }}
                    </b-button>
                </div>
            </template>

            <template v-slot:cell(created_at)="data">
                {{ data.item.created_at | date_time_om }}
            </template>

            <template v-slot:cell(status)="data">
                <div class="plagiarism-status">
                    <b-badge
                        v-if="data.item.status === 'checked'"
                        pill
                        variant="primary">
                        {{ $t('roadmap.ready') }}
                    </b-badge>
                    <b-badge
                        v-else-if="['error', 'server error', 'not paid'].includes(data.item.status)"
                        pill
                        variant="danger">
                        {{ $t('roadmap.failed') }}
                    </b-badge>
                    <b-badge
                        v-else
                        pill
                        variant="warning">
                        {{ $t('roadmap.is_processed') }}
                    </b-badge>
                </div>
            </template>

            <template v-slot:cell(plagiarism_percentage)="data">
                <div v-if="data.item.plagiarism_percentage">{{ data.item.plagiarism_percentage }}&nbsp;%</div>
                <div v-else>{{ $t('roadmap.no_result') }}</div>
            </template>

            <template v-slot:cell(actions)="data">
                <div
                    class="d-flex align-center"
                    v-if="PLAGIARISM_REPORT_FINAL_STATUSES().includes(data.item.status)">
                    <b-button
                        class="btn-open btn-open-plagiarism-report"
                        v-if="data.item.view_edit_url"
                        v-b-tooltip.hover
                        variant="primary-icon-only"
                        :title="$t('roadmap.view_report')"
                        target="_blank"
                        :href="data.item.view_edit_url">
                    </b-button>

                    <b-button
                        class="btn-open btn-open-plagiarism-report"
                        v-if="data.item.pdf_url"
                        v-b-tooltip.hover
                        variant="primary-icon-only"
                        :title="$t('roadmap.view_report')"
                        target="_blank"
                        :href="data.item.html_url">
                    </b-button>

                    <b-button
                        class="btn-download btn-plagiarism-show btn-download-plagiarism-report"
                        v-if="data.item.pdf_url"
                        v-b-tooltip.hover
                        variant="primary-icon-only"
                        :title="$t('roadmap.download_pdf_report')"
                        target="_blank"
                        :href="data.item.pdf_url">
                    </b-button>
                </div>

                <div v-else>
                    <span class="btn-icon btn-busy btn-info remove-icon-margin"></span>
                </div>
            </template>
        </design-2021-apprentice-data-table>

        <div
            class="not-found with-header"
            v-else>
            <div class="header">
                <b-button
                    class="btn-add btn-submit-for-plagiarism-check"
                    variant="primary"
                    @click="submitForPlagiarismCheck">
                    {{ $t('roadmap.request_new_check') }}
                </b-button>
            </div>

            <div class="icon">
                <i class="fas fa-exclamation-circle"></i>
            </div>
            <div class="alert-notice">{{ $t('roadmap.no_files_found_to_check') }}</div>
        </div>
    </b-modal>
</template>

<script>
import Design2021ApprenticeDataTable from '../../../common/design-2021-apprentice-data-table.vue';
import { PLAGIARISM_REPORT_FINAL_STATUSES } from '../../../../helpers/globalConstants';

export default {
    name: 'design-2021-paper-powerpoint-plagiarism-modal-component',

    components: { Design2021ApprenticeDataTable },

    props: {
        editorFileIdIn: {
            type: Number,
            required: true,
        },
    },

    data() {
        return {
            getReportInterval: null,
            reports: [],
            fields: [
                {
                    key: 'created_at',
                    label: this.$t('roadmap.requested'),
                    thClass: 'p-3',
                    tdClass: 'p-3',
                    sortable: true,
                },
                {
                    key: 'status',
                    label: 'Status',
                    thClass: 'p-3',
                    tdClass: 'p-3',
                },
                {
                    key: 'plagiarism_percentage',
                    label: this.$t('roadmap.plagiarism'),
                    thClass: 'p-3',
                    tdClass: 'p-3',
                },
                {
                    key: 'actions',
                    label: this.$t('roadmap.report'),
                    thClass: 'p-3',
                    tdClass: 'p-3',
                },
            ],
        };
    },

    methods: {
        PLAGIARISM_REPORT_FINAL_STATUSES() {
            return PLAGIARISM_REPORT_FINAL_STATUSES;
        },

        onShow() {
            this.getReports();
            this.startGetReportsInterval();
        },

        onHide() {
            clearInterval(this.getReportInterval);
        },

        startGetReportsInterval() {
            const self = this;

            this.getReportInterval = setInterval(function () {
                if (
                    self.reports.length > 0 &&
                    self.reports.every((report) => PLAGIARISM_REPORT_FINAL_STATUSES.includes(report.status))
                ) {
                    clearInterval(self.getReportInterval);
                } else {
                    self.getReports();
                }
            }, 2000);
        },

        submitForPlagiarismCheck() {
            this.alert.saving();

            this.axios
                .post(this.routes.requestPlagiarismReportForEditorFile(this.editorFileIdIn))
                .then((response) => {
                    if (this.reports.some((report) => report.external_id === response.data.external_id)) {
                        this.alert.notice(
                            this.$t('roadmap.plagiarism_report_already_exists'),
                            this.$t('roadmap.a_plagiarism_report_is_already_requested_for_this_file'),
                        );

                        this.reports = this.reports.filter(
                            (report) => report.external_id !== response.data.external_id,
                        );
                        this.reports.unshift(response.data);
                        return;
                    }

                    this.reports.unshift(response.data);

                    if (PLAGIARISM_REPORT_FINAL_STATUSES.includes(response.data.status)) {
                        this.alert.notice(
                            this.$t('roadmap.plagiarism_report_completed'),
                            this.$t('roadmap.plagiarism_report_is_completed_the_report_is_ready'),
                        );
                    } else {
                        this.alert.notice(
                            this.$t('roadmap.plagiarism_report_requested'),
                            this.$t(
                                'roadmap.plagiarism_report_is_started_it_can_take_a_few_minutes_before_the_report_is_ready',
                            ),
                        );

                        this.startGetReportsInterval();
                    }
                })
                .catch(() => {
                    this.alert.notice(
                        this.$t('roadmap.no_control'),
                        this.$t(
                            'roadmap.something_went_wrong_while_carrying_out_the_plagiarism_report_please_try_again_later',
                        ),
                    );
                });
        },

        getReports() {
            this.axios
                .get(this.routes.getPlagiarismReportsForEditorFile(this.editorFileIdIn))
                .then((response) => (this.reports = response.data))
                .catch(() => this.alert.ajaxError(error));
        },
    },
};
</script>

<style scoped lang="scss">
.plagiarism-status {
    .badge {
        font-size: 0.8125rem;
        padding: 0.35em 0.8em;
    }
}
</style>
